import * as styles from './Button.module.scss';

import cn from 'classnames';
import React from 'react';
import Icon, { IconPropView } from 'src/components/ui/Icon';
import Loader from 'src/components/ui/Loader';
import Text from 'src/components/ui/Text';

type ButtonAnchor = {
  element: 'a';
  href: string;
  target: '_blank';
} & React.HTMLProps<HTMLAnchorElement>;

type ButtonDefault = {
  element: 'button';
} & React.HTMLProps<HTMLButtonElement>;

type ButtonCommonProps = {
  element?: string;
} & React.HTMLProps<HTMLElement>;

export const ButtonView = ['primary', 'additional', 'clear', 'ghost', 'icon', 'secondary'] as const;
export type ButtonPropView = typeof ButtonView[number];

// TODO доделать дженерик
export type ButtonProps = {
  view?: ButtonPropView;
  disabled?: boolean;
  isLoading?: boolean;
  iconBefore?: IconPropView;
  iconAfter?: IconPropView;
  value?: any;
  className?: string;
  classText?: String;

  onClickWithValue?: (value: any) => void;
} & (ButtonCommonProps | ButtonDefault | ButtonAnchor);

const Button = React.forwardRef<HTMLElement, ButtonProps>(
  (
    {
      onClickWithValue,
      value,
      disabled,
      iconBefore,
      className,
      classText,
      iconAfter,
      view = 'primary',
      isLoading,
      element = 'button',
      children,
      ...props
    },
    ref
  ) => {
    const Element = element as React.ElementType;
    const isOnlyIcon = !children && (iconBefore || iconAfter);

    const handlerClick = React.useCallback(
      (e: React.MouseEvent) => {
        if (onClickWithValue) {
          e.preventDefault();
          e.stopPropagation();
          onClickWithValue(value);
        }
      },
      [onClickWithValue, value]
    );

    return (
      <Element
        ref={ref}
        className={cn(
          styles.button,
          view && styles[`button_${view}`],
          isLoading && styles.button_loading,
          isOnlyIcon && styles.button_onlyIcon,
          disabled && styles.button_disabled,
          className
        )}
        onClick={handlerClick}
        {...props}
      >
        {iconBefore && <Icon className={cn(styles.button__icon, styles.button__icon_before)} view={iconBefore} />}
        <Text className={cn(styles.button__text, classText)}  view="button">
          {children}
        </Text>
        {iconAfter && <Icon className={cn(styles.button__icon, styles.button__icon_after)} view={iconAfter} />}
        {isLoading && <Loader className={styles.button__loader} />}
      </Element>
    );
  }
);

export default React.memo(Button);
